// Phase 43.17 — Admin user edit drawer (slide-right) + audit history.
// Side-drawer simile a OrderDetailsDrawer ma in modalità modifica.
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Send, X, History, KeyRound, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime, ROLE_LABEL } from "@/lib/format";
import AdminUserSessions from "@/components/admin/AdminUserSessions";

const ACCOUNT_TYPES = [
  { value: "privato", label: "Privato" },
  { value: "business", label: "Business / Azienda" },
  { value: "shop", label: "Negozio" },
];

const ACCOUNT_STATUSES = [
  { value: "active", label: "Attivo" },
  { value: "suspended", label: "Sospeso" },
];

const SUBSCRIPTION_PLANS = [
  { value: "free", label: "Free" },
  { value: "business_start", label: "Business Start" },
  { value: "business_pro", label: "Business Pro" },
  { value: "enterprise", label: "Enterprise" },
];

const ROLE_TO_TYPE = {
  customer_private: "privato",
  customer_business: "business",
  customer_shop: "shop",
};

const SENSITIVE = new Set([
  "email", "phone", "phone_prefix",
  "account_type", "account_status", "subscription_plan",
]);

const initialForm = (u) => ({
  name: u?.name || "",
  business_name: u?.business_name || "",
  email: u?.email || "",
  phone_prefix: u?.phone_prefix || "+41",
  phone: u?.phone || "",
  account_type: ROLE_TO_TYPE[u?.role] || "",
  account_status: u?.account_status || "active",
  subscription_plan: u?.subscription_plan || "free",
  admin_internal_notes: u?.admin_internal_notes || "",
  // primary_address as object
  addr_street: u?.primary_address?.street || "",
  addr_house: u?.primary_address?.house_number || "",
  addr_zip: u?.primary_address?.zip || "",
  addr_city: u?.primary_address?.city || "",
  addr_canton: u?.primary_address?.canton || "",
  addr_country: u?.primary_address?.country || "Svizzera",
});

export default function AdminUserEditDrawer({ user, onClose, onSaved }) {
  const [form, setForm] = useState(() => initialForm(user));
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => { setForm(initialForm(user)); setReason(""); }, [user]);

  const loadHistory = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/admin/audit-logs", {
        params: { target_type: "user", target_id: user.id, limit: 50 },
      });
      setHistory(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Build "changes" payload (solo campi effettivamente modificati)
  const changes = useMemo(() => {
    if (!user) return {};
    const c = {};
    const fields = [
      "name", "business_name", "email",
      "phone", "phone_prefix",
      "account_type", "account_status", "subscription_plan",
      "admin_internal_notes",
    ];
    const original = initialForm(user);
    for (const f of fields) {
      if ((form[f] || "") !== (original[f] || "")) c[f] = form[f] || null;
    }
    // primary_address come dict
    const newAddr = {
      street: form.addr_street || null,
      house_number: form.addr_house || null,
      zip: form.addr_zip || null,
      city: form.addr_city || null,
      canton: form.addr_canton || null,
      country: form.addr_country || "Svizzera",
    };
    const oldAddr = user.primary_address || null;
    const hasAddr = newAddr.street || newAddr.zip || newAddr.city;
    const addrChanged =
      (!oldAddr && hasAddr) ||
      (oldAddr && JSON.stringify(oldAddr) !== JSON.stringify(newAddr));
    if (addrChanged) c.primary_address = hasAddr ? newAddr : null;
    return c;
  }, [form, user]);

  const hasSensitive = Object.keys(changes).some((k) => SENSITIVE.has(k));
  const changedCount = Object.keys(changes).length;
  const reasonRequired = hasSensitive && !reason.trim();

  const save = async () => {
    if (changedCount === 0) {
      toast("Nessuna modifica da salvare.");
      return;
    }
    if (hasSensitive && !reason.trim()) {
      toast.error("Indica un motivo: stai modificando campi sensibili.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/users/${user.id}`, {
        changes,
        reason: reason.trim() || null,
      });
      toast.success(`Salvate ${data.changed} modifiche.`);
      onSaved?.(data.user);
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendResetLink = async () => {
    if (!window.confirm(`Inviare un link reset password a ${user.email}?`)) return;
    setSendingReset(true);
    try {
      await api.post(`/admin/users/${user.id}/send-password-reset`);
      toast.success("Link reset inviato (valido 1 ora).");
      if (showHistory) loadHistory();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSendingReset(false);
    }
  };

  // Phase 44.05 — Force reset password
  const [forceResetOpen, setForceResetOpen] = useState(false);
  const [forceResetPwd, setForceResetPwd] = useState("");
  const [forceResetPwd2, setForceResetPwd2] = useState("");
  const [forceLogout, setForceLogout] = useState(true);
  const [forceResetReason, setForceResetReason] = useState("");
  const [forcingReset, setForcingReset] = useState(false);

  const forceReset = async () => {
    if (forceResetPwd.length < 8) { toast.error("Password troppo corta (min 8)."); return; }
    if (forceResetPwd !== forceResetPwd2) { toast.error("Le password non coincidono."); return; }
    setForcingReset(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/force-password-reset`, {
        new_password: forceResetPwd,
        force_logout: forceLogout,
        reason: forceResetReason || undefined,
      });
      toast.success(
        forceLogout
          ? "Password impostata. Tutte le sessioni utente sono state revocate."
          : "Password impostata."
      );
      setForceResetOpen(false);
      setForceResetPwd(""); setForceResetPwd2(""); setForceResetReason("");
      if (showHistory) loadHistory();
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setForcingReset(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-40"
        onClick={onClose}
        data-testid="admin-user-edit-backdrop"
      />
      <aside
        className="fixed top-0 right-0 z-50 h-full w-full sm:w-[520px] bg-white shadow-2xl overflow-y-auto"
        data-testid="admin-user-edit-drawer"
      >
        <div className="sticky top-0 bg-white border-b border-dapnow-line px-5 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <p className="overline text-[10px]">Modifica cliente</p>
            <p className="font-display text-lg font-bold tracking-tight truncate">{user.name}</p>
            <p className="text-xs text-dapnow-muted truncate">{ROLE_LABEL[user.role]} · {user.email}</p>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-user-edit-close"
            className="rounded-lg border border-dapnow-line h-9 w-9 inline-flex items-center justify-center hover:border-dapnow-blue text-sm"
            aria-label="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Dati base */}
          <Section title="Dati base">
            <Field label="Nome / Referente">
              <input data-testid="user-edit-name" className="input-tall w-full"
                value={form.name} onChange={(e) => onField("name", e.target.value)} />
            </Field>
            <Field label="Azienda / Ragione sociale">
              <input data-testid="user-edit-business-name" className="input-tall w-full"
                value={form.business_name} onChange={(e) => onField("business_name", e.target.value)} />
            </Field>
            <Field label={<>Email <SensitiveDot /></>}>
              <input type="email" data-testid="user-edit-email" className="input-tall w-full"
                value={form.email} onChange={(e) => onField("email", e.target.value)} />
            </Field>
            <div className="grid grid-cols-[100px_1fr] gap-2">
              <Field label={<>Prefisso <SensitiveDot /></>}>
                <select className="input-tall w-full" data-testid="user-edit-phone-prefix"
                  value={form.phone_prefix} onChange={(e) => onField("phone_prefix", e.target.value)}>
                  <option value="+41">+41</option>
                  <option value="+39">+39</option>
                </select>
              </Field>
              <Field label={<>Telefono <SensitiveDot /></>}>
                <input data-testid="user-edit-phone" className="input-tall w-full"
                  value={form.phone} onChange={(e) => onField("phone", e.target.value)} />
              </Field>
            </div>
          </Section>

          {/* Account */}
          <Section title="Account">
            <Field label={<>Tipo account <SensitiveDot /></>}>
              <select data-testid="user-edit-account-type" className="input-tall w-full"
                value={form.account_type} onChange={(e) => onField("account_type", e.target.value)}>
                <option value="">— invariato —</option>
                {ACCOUNT_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label={<>Stato account <SensitiveDot /></>}>
              <select data-testid="user-edit-account-status" className="input-tall w-full"
                value={form.account_status} onChange={(e) => onField("account_status", e.target.value)}>
                {ACCOUNT_STATUSES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
            <Field label={<>Piano business <SensitiveDot /></>}>
              <select data-testid="user-edit-subscription-plan" className="input-tall w-full"
                value={form.subscription_plan} onChange={(e) => onField("subscription_plan", e.target.value)}>
                {SUBSCRIPTION_PLANS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </Field>
          </Section>

          {/* Indirizzo principale */}
          <Section title="Indirizzo principale">
            <div className="grid grid-cols-[1fr_100px] gap-2">
              <Field label="Via">
                <input data-testid="user-edit-addr-street" className="input-tall w-full"
                  value={form.addr_street} onChange={(e) => onField("addr_street", e.target.value)} />
              </Field>
              <Field label="N.">
                <input data-testid="user-edit-addr-house" className="input-tall w-full"
                  value={form.addr_house} onChange={(e) => onField("addr_house", e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-[100px_1fr_120px] gap-2">
              <Field label="CAP">
                <input data-testid="user-edit-addr-zip" className="input-tall w-full"
                  value={form.addr_zip} onChange={(e) => onField("addr_zip", e.target.value)} />
              </Field>
              <Field label="Città">
                <input data-testid="user-edit-addr-city" className="input-tall w-full"
                  value={form.addr_city} onChange={(e) => onField("addr_city", e.target.value)} />
              </Field>
              <Field label="Cantone">
                <input data-testid="user-edit-addr-canton" className="input-tall w-full"
                  value={form.addr_canton} onChange={(e) => onField("addr_canton", e.target.value)} />
              </Field>
            </div>
          </Section>

          {/* Note admin */}
          <Section title="Note admin interne">
            <textarea
              data-testid="user-edit-admin-notes"
              className="input-tall w-full min-h-[80px]"
              value={form.admin_internal_notes}
              placeholder="Visibili solo agli admin (non al cliente)."
              onChange={(e) => onField("admin_internal_notes", e.target.value)}
            />
          </Section>

          {/* Password */}
          <Section title="Password">
            <div className="space-y-2">
              <button
                onClick={sendResetLink}
                disabled={sendingReset}
                data-testid="user-edit-send-reset"
                className="inline-flex items-center gap-2 rounded-lg border border-dapnow-line bg-white px-3 h-10 text-sm font-semibold hover:border-dapnow-blue hover:text-dapnow-blue disabled:opacity-50 w-full justify-center"
              >
                <Send className="h-4 w-4" />
                {sendingReset ? "Invio…" : "Invia link reset password"}
              </button>
              <button
                onClick={() => setForceResetOpen(true)}
                data-testid="user-edit-force-reset-open"
                className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white text-red-700 px-3 h-10 text-sm font-semibold hover:bg-red-50 w-full justify-center"
              >
                <KeyRound className="h-4 w-4" />
                Imposta password manuale (force)
              </button>
            </div>
            <p className="text-xs text-dapnow-muted mt-1">
              Il link inviato è valido 1h. Il force reset permette all'admin di impostare direttamente una nuova password (es. quando il cliente non ha accesso all'email).
            </p>
          </Section>

          {/* Reason */}
          {hasSensitive && (
            <Section title="Motivo modifica (obbligatorio)">
              <input
                data-testid="user-edit-reason"
                className="input-tall w-full"
                placeholder="Es: aggiornamento dati dopo segnalazione cliente"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
              <p className="text-[11px] text-amber-700 mt-1">
                Stai modificando campi sensibili. Il motivo finirà nello storico modifiche.
              </p>
            </Section>
          )}

          {/* Phase 44.31 — Sessioni attive */}
          <AdminUserSessions userId={user.id} />

          {/* History */}
          <Section
            title={
              <button
                type="button"
                onClick={() => { const next = !showHistory; setShowHistory(next); if (next) loadHistory(); }}
                data-testid="user-edit-toggle-history"
                className="inline-flex items-center gap-2 text-left"
              >
                <History className="h-4 w-4" /> Storico modifiche {showHistory ? "▾" : "▸"}
              </button>
            }
          >
            {showHistory && (
              <div className="space-y-2 max-h-60 overflow-y-auto" data-testid="user-edit-history-list">
                {history.length === 0 && <p className="text-xs text-dapnow-muted">Nessuna modifica registrata.</p>}
                {history.map((h) => (
                  <div key={h.id} className="text-xs border border-dapnow-line rounded-lg p-2">
                    <p className="font-semibold text-dapnow-ink">{h.field}</p>
                    <p className="text-dapnow-muted">
                      <span className="line-through">{formatVal(h.old_value)}</span>
                      {" → "}
                      <span className="text-dapnow-ink">{formatVal(h.new_value)}</span>
                    </p>
                    <p className="text-dapnow-muted mt-1">
                      {h.admin_email || h.admin_name} · {formatDateTime(h.created_at)}
                    </p>
                    {h.reason && <p className="italic text-dapnow-muted">"{h.reason}"</p>}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>

        {/* Sticky save bar */}
        <div className="sticky bottom-0 bg-white border-t border-dapnow-line px-5 py-3 flex items-center justify-between gap-3">
          <p className="text-xs text-dapnow-muted">
            {changedCount === 0 ? "Nessuna modifica" : `${changedCount} campo${changedCount > 1 ? "i" : ""} modificat${changedCount > 1 ? "i" : "o"}`}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded-lg border border-dapnow-line px-3 h-10 text-sm font-semibold hover:bg-dapnow-bg"
              data-testid="user-edit-cancel"
            >
              Annulla
            </button>
            <button
              onClick={save}
              disabled={saving || changedCount === 0 || reasonRequired}
              className="inline-flex items-center gap-2 rounded-lg bg-dapnow-ink text-white px-4 h-10 text-sm font-semibold disabled:opacity-50"
              data-testid="user-edit-save"
              title={reasonRequired ? "Indica un motivo per i campi sensibili" : undefined}
            >
              <Save className="h-4 w-4" />
              {saving ? "Salvataggio…" : "Salva"}
            </button>
          </div>
        </div>
      </aside>
      {forceResetOpen && (
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
          data-testid="force-reset-modal"
          onClick={() => !forcingReset && setForceResetOpen(false)}
        >
          <div
            className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl p-5 sm:p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-700 shrink-0">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <div>
                <h3 className="font-display text-lg font-bold tracking-tight">Imposta password manuale</h3>
                <p className="text-xs text-dapnow-muted mt-0.5">
                  Stai per impostare direttamente una nuova password per <strong>{user.email}</strong>.
                  Comunicala al cliente in modo sicuro fuori-banda.
                </p>
              </div>
            </div>

            <div className="space-y-2.5">
              <div>
                <label className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted">Nuova password (min 8)</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  data-testid="force-reset-new-password"
                  value={forceResetPwd}
                  onChange={(e) => setForceResetPwd(e.target.value)}
                  className="input-tall w-full mt-1 font-mono"
                  placeholder="••••••••"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted">Conferma password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  data-testid="force-reset-new-password-2"
                  value={forceResetPwd2}
                  onChange={(e) => setForceResetPwd2(e.target.value)}
                  className="input-tall w-full mt-1 font-mono"
                  placeholder="••••••••"
                />
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="force-reset-force-logout"
                  checked={forceLogout}
                  onChange={(e) => setForceLogout(e.target.checked)}
                  className="h-4 w-4 rounded border-dapnow-line accent-dapnow-blue"
                />
                <span>Revoca tutte le sessioni attive dell'utente</span>
              </label>
              <div>
                <label className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted">Motivo (opzionale)</label>
                <input
                  data-testid="force-reset-reason"
                  value={forceResetReason}
                  onChange={(e) => setForceResetReason(e.target.value)}
                  className="input-tall w-full mt-1"
                  placeholder="Es: cliente senza accesso all'email"
                />
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 pt-1">
              <button
                onClick={() => setForceResetOpen(false)}
                disabled={forcingReset}
                data-testid="force-reset-cancel"
                className="rounded-lg border border-dapnow-line px-3 h-10 text-sm font-semibold hover:bg-dapnow-bg flex-1 disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                onClick={forceReset}
                disabled={forcingReset || forceResetPwd.length < 8 || forceResetPwd !== forceResetPwd2}
                data-testid="force-reset-confirm"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-red-600 text-white px-4 h-10 text-sm font-semibold hover:bg-red-700 disabled:opacity-50 flex-1"
              >
                <KeyRound className="h-4 w-4" />
                {forcingReset ? "Aggiornamento…" : "Imposta password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <p className="overline mb-2">{title}</p>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted">{label}</span>
      {children}
    </label>
  );
}

function SensitiveDot() {
  return <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500 ml-1 align-middle" title="Campo sensibile — motivo obbligatorio" />;
}

function formatVal(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}
